import { useCallback, useContext, useEffect, useRef, useState } from "react";
import TableStyle from "styles/application/TableStyle";
import { makeStyles } from "tss-react/mui";
import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import ColourConstants from "helpers/colourConstants";
import { ModelContext } from "contexts/ModelDetailContext";
import { useTranslation } from "react-i18next";
import { patchModelTaskZone } from "services/models/modelDetails/modelTaskZones";
import { showError } from "../../../../../redux/common/actions";
import { useDispatch } from "react-redux";
import ZoneRow from "./ZoneRow";
import { useModelTaskListSyncContext } from "contexts/ModelTaskListSyncContext";
import { getSiteAssetRegistersLookup } from "services/clients/sites/siteAssetRegisters";

const AT = TableStyle();

const debounce = (func, delay) => {
	let timer;
	return function () {
		let self = this;
		let args = arguments;
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(self, args);
		}, delay);
	};
};

const useStyles = makeStyles()((theme) => ({
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	nameRow: {
		width: "200px",
		height: "10px",
		lineHeight: "1rem",
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 40,
	},
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
}));

function TaskZoneListTable({
	data,
	handleSelectZone,
	siteAssset,
	fetchSiteAssest,
	siteAppId,
	setSiteAssest,
	assestCount,
	setZones,
	setOriginalZones,
	originalZones,
	customCaptions,
	isReadOnly,
	fetchSiteFromDropDown,
	fetchModelTaskZones,
	taskId,
	showAssetReferences,
}) {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [modelState] = useContext(ModelContext);
	const { patchTask } = useModelTaskListSyncContext();
	const [page, setPage] = useState({ pageNo: 1, pageSize: 10,  sortField:'', sortOrder:'' });
    const pageRef = useRef(page)
	const {
		modelDetail: { modelType },
	} = modelState;

	useEffect(()=>{ pageRef.current= page},[JSON.stringify(page)])
	

	const onPageChange = async (pageNo) => {
    	const {current} = pageRef;
		setPage((prev) => ({ ...prev, pageNo }));
		await fetchSiteAssest(siteAppId, pageNo, page.pageSize,"", current.sortField,
		current.sort);
	};

	const handleServerSideSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt) {
				const response = await getSiteAssetRegistersLookup(
					null,
					siteAppId,
					1,
					100,
					searchTxt
				);
				if (response.status) {
					setSiteAssest(response.data);
				}
			} else {
				onPageChange(1);
			}
		}, 500),
		[]
	);

	const onDropDownChange = async (zoneId, list) => {
		if (list) {
			setZones(
				data.map((z) =>
					z.id === zoneId
						? {
								...z,

								siteAssetRegisterID: list.id,
								siteAssetRegisterAssetNumber: list.assetNumber || list.name,
						  }
						: z
				)
			);

			try {
				const response = await patchModelTaskZone(zoneId, [
					{
						op: "replace",
						path: "siteAssetRegisterID",
						value: list.id || null,
					},
				]);
				if (response.status) {
					setOriginalZones(data);
					fetchModelTaskZones(false);
					patchTask(taskId, { assets: response?.data?.assets });
				} else {
					dispatch(
						showError(response?.data?.detail || t("message.couldNotAddAssetToZone"))
					);
					setZones(originalZones);
				}
			} catch (error) {
				dispatch(
					showError(error?.response?.data || t("message.couldNotAddAssetToZone"))
				);
				setZones(originalZones);
			}
		}
	};

	 const handleCustomSort = async (sortField, sortOrder) => {
		// Map column data field names to API field names
		let apiSortField = "";
		switch (sortField) {
			case "name":
				apiSortField = "name"; // Backend accepts "name" or "assetno"
				break;
			case "assetReferenceName":
				apiSortField = "referencename";
				break;
			case "assetReferenceDescription":
			case "description":
				apiSortField = "description";
				break;
			default:
				apiSortField = sortField;
		}

		setPage({ pageNo: 1, pageSize: page.pageSize, sortField: apiSortField, sortOrder });
		setSiteAssest([]);
		await fetchSiteAssest(siteAppId, 1, page.pageSize, "", apiSortField, sortOrder);
	  };

	return (
		<div>
			<Table aria-label="Table" className={classes.table}>
				<AT.TableHead>
					<TableRow className={classes.tableHead}>
						<TableCell
							style={{ width: "78px" }}
							className={cx(classes.nameRow, {
								[classes.tableHeadRow]: true,
							})}
						>
							{t("label.selected")}
						</TableCell>
						<TableCell
							style={{ width: "auto" }}
							className={cx(classes.nameRow, {
								[classes.tableHeadRow]: true,
							})}
						>
							{t("label.name")}
						</TableCell>
						{modelType === "F" || modelType === "A" ? (
							<TableCell
								style={{ width: "auto" }}
								className={cx(classes.nameRow, {
									[classes.tableHeadRow]: true,
								})}
							>
								{t("label.image")}
							</TableCell>
						) : null}
						{modelType === "F" ? (
							<TableCell
								style={{ width: "auto" }}
								className={cx(classes.nameRow, {
									[classes.tableHeadRow]: true,
								})}
							>
								{customCaptions?.asset}
							</TableCell>
						) : null}
					</TableRow>
				</AT.TableHead>
				<TableBody>
					{data.length !== 0 ? (
						data.map((row) => (
							<ZoneRow
								key={row?.modelVersionZoneID}
								row={row}
								handleSelectZone={handleSelectZone}
								modelType={modelType}
								siteAssset={siteAssset}
								onPageChange={onPageChange}
								setSiteAssest={setSiteAssest}
								page={page}
								assestCount={assestCount}
								handleServerSideSearch={handleServerSideSearch}
								onDropDownChange={onDropDownChange}
								handleCustomSort={handleCustomSort}
								isReadOnly={isReadOnly}
								customCaptions={customCaptions}
								fetchSiteFromDropDown={fetchSiteFromDropDown}
								fetchModelTaskZones={fetchModelTaskZones}
								showAssetReferences={showAssetReferences}
							/>
						))
					) : (
						<TableRow>
							<TableCell>{t("message.noRecordsFound")}</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}

export default TaskZoneListTable;
